import FormViewForm from '@src/components/_forms/FormViewForm';
import OrderFormFields from '@views/orders/OrderFormFields';
import {IOrderModel} from '@src/@interfaces/models/Order/IOrderModel';
import OrderProductsField from '@views/orders/fields/products/OrderProductsField';
import OrderPaymentsField from '@views/orders/fields/payments/OrderPaymentsField';
import OrderLogsField from '@views/orders/fields/logs/OrderLogsField';
import OrderAttentionsField from '@views/orders/fields/attentions/OrderAttentionsField';
import OrderCommentsField from '@views/orders/fields/comments/OrderCommentsField';
import OrderStatusIdField from '@views/orders/fields/order_status_id/OrderStatusIdField';
import OrderTasksField from '@views/orders/fields/tasks/OrderTasksField';
import {IOrderProductModel} from '@src/@interfaces/models/Order/IOrderProductModel';
import OrderFormSubscriber from '@views/orders/OrderFormSubscriber';
import OrderFormMainTabContentWrapper from '@views/orders/OrderFormMainTabContentWrapper';
import VoidField from '@views/orders/VoidField';
import OrderChecklistField from '@views/orders/fields/checklist/OrderChecklistField';
import {OrderProductTechProcess} from '@views/orders/fields/products/extra_row/tech-process/.types';
import {
    IOrderProductTechProcessModel
} from '@src/@interfaces/models/Order/ProductTechProcess/IOrderProductTechProcessModel';
import OrderDescriptionField from '@views/orders/fields/OrderDescriptionField';
import app from '@src/helpers/app';
import OrderResidualPriceField from '@views/orders/fields/OrderResidualPriceField';

const customFields = {
    residual_price: {
        component: OrderResidualPriceField,
    },
    manager: {
        component: VoidField,
    },
    client: {
        component: VoidField,
    },
    deadline_at: {
        component: VoidField,
    },
    description: {
        component: OrderDescriptionField,
    },
    products: {
        component: OrderProductsField,
    },
    payments: {
        component: OrderPaymentsField,
    },
    logs: {
        component: OrderLogsField,
    },
    attentions: {
        component: OrderAttentionsField,
    },
    comments: {
        component: OrderCommentsField,
    },
    status: {
        component: OrderStatusIdField,
    },
    _tasks: {
        component: OrderTasksField,
    },
    _checklist: {
        component: OrderChecklistField,
    },
};

const customTabsContentWrappers = {'default': OrderFormMainTabContentWrapper};

export function getProductTechProcessInitialValue(techProcess: IOrderProductTechProcessModel | undefined | null) {
    const result: OrderProductTechProcess = {
        id: null,
        enabled: false,
        variables_set_id: null,
        definedVariablesValues: [],
        operations: [],

        ...(techProcess ?? {}),
    };
    result.definedVariablesValues = techProcess?.variables_values ?? [];
    result.operations = (techProcess?.operations ?? []).map(operation => {
        const result = {...operation};
        result.tech_operation_materials = operation.materials ?? [];
        if ('materials' in operation) {
            delete result['materials'];
        }

        return result;
    });

    if ('variables_values' in result) {
        delete result['variables_values'];
    }

    return result;
}

export default class OrderForm<Model extends IOrderModel> extends FormViewForm<Model> {
    formFieldsComponent = OrderFormFields;
    formSubscriberComponent = OrderFormSubscriber;

    protected get customFields() {
        return customFields;
    }

    protected get customTabsContentWrappers() {
        return customTabsContentWrappers;
    }

    protected get initialValues() {
        const res = super.initialValues;
        if ('logs' in res) {
            delete res['logs'];
        }

        if (!this.props.model?.id) {
            const user = app('auth').getUser();
            if (!res.manager && !user?.is_super_user && user?.can('users@is-manager')) {
                res.manager = user?.id;
            }

            if (!res.status && Array.isArray(this.props.modelOptions?.status)) {
                res.status = this.props.modelOptions?.status.find(item => item.is_new === true)?.value;
            }
        }

        return res;
    }

    protected async onSubmit(data: Record<string, any>): Promise<any> {
        if ('products' in data && Array.isArray(data['products'])) {
            data['products'] = data['products'].map(itemData => {
                [
                    'status',
                    'properties',
                    'previews_public_path',
                    'checked_checklists_items',
                ].forEach(key => {
                    if (key in itemData) {
                        delete itemData[key];
                    }
                });

                return itemData;
            });
        }

        return super.onSubmit(data);
    }

    protected mutateModelFieldInitialValue(fieldName: string, modelValue: any): any {
        if (fieldName === 'products') {
            return this.getProductsFieldInitialValue();
        }

        if (fieldName === 'payments') {
            return this.getPaymentsFieldInitialValue();
        }

        return super.mutateModelFieldInitialValue(fieldName, modelValue);
    }

    protected getProductInitialData(product: Partial<IOrderProductModel>) {
        const res: Record<string, any> = {
            ...product,
            previews: [],
            custom_price: product.custom_price != null ?
                    product.custom_price / 100 :// TODO: it has to be replaced by PriceField somehow
                    null,
            techProcesses: this.getProductTechProcessInitialValue(product) ?? null,
        };

        ['files', 'order', 'attention', 'product', 'created_at', 'updated_at'].forEach(key => {
            if (key in res) {
                delete (res as any)[key];
            }
        });

        return res;
    }

    private getProductTechProcessInitialValue(product: Partial<IOrderProductModel>) {
        return product.tech_processes?.map(item => getProductTechProcessInitialValue(item));
    }

    private getProductsFieldInitialValue() {
        return this.props.model?.products?.map(product => this.getProductInitialData(product));
    }

    private getPaymentsFieldInitialValue() {
        return this.props.model?.payments?.map(payment => ({
            ...payment,
            amount: payment.amount != null ?
                    Math.floor(payment.amount / 100) :// TODO: it has to be replaced by PriceField somehow
                    null,
            fields: payment.additional_fields,
        }));
    }
}
